import { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Modal from '../Modal/Modal';
import { faGift } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../context/AuthProvider';
import { API_BASE_URL } from '../../api';

export default function RedeemPointsModal({ show, onClose, card, onSuccess }) {
    const { settings } = useAuth();
    const redeemRatio = Number(settings['loyalty_redeem_ratio'] ?? 0.10);

    const [points, setPoints] = useState('');
    const [loading, setLoading] = useState(false);

    const pointsNum = parseInt(points, 10) || 0;
    const discount = (pointsNum * redeemRatio).toFixed(2);
    const isValid = pointsNum > 0 && pointsNum <= (card?.currentPoints ?? 0);

    const handleSubmit = async () => {
        if (!isValid) return;
        setLoading(true);
        try {
            await axios.post(
                `${API_BASE_URL}/api/v1/loyalty-cards/${card.id}/redeem`,
                { points: pointsNum },
                { withCredentials: true }
            );
            toast.success(`${pointsNum} points utilisés — réduction de ${discount} dhs appliquée`);
            setPoints('');
            onSuccess?.();
            onClose?.();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Erreur lors de l\'utilisation des points');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setPoints('');
        onClose?.();
    };

    return (
        <Modal
            show={show}
            onClose={handleClose}
            title="Utiliser des points"
            icon={faGift}
            iconBg="#eff6ff"
            iconColor="#2563eb"
            footer={
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button
                        onClick={handleClose}
                        style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
                    >
                        Annuler
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={!isValid || loading}
                        style={{
                            padding: '8px 18px', borderRadius: 8, border: 'none',
                            background: isValid ? '#2563eb' : '#e5e7eb',
                            color: isValid ? '#fff' : '#9ca3af',
                            cursor: isValid ? 'pointer' : 'not-allowed',
                            fontSize: 13, fontWeight: 600,
                        }}
                    >
                        {loading ? 'Traitement...' : 'Confirmer'}
                    </button>
                </div>
            }
        >
            <div style={{ padding: '8px 0', display: 'flex', flexDirection: 'column', gap: 20 }}>
                {/* Balance info */}
                <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{ flex: 1, background: '#f9fafb', borderRadius: 10, padding: '14px', textAlign: 'center' }}>
                        <div style={{ fontSize: 22, fontWeight: 800, color: '#111827' }}>
                            {card?.currentPoints ?? 0}
                        </div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Points disponibles</div>
                    </div>
                    <div style={{ flex: 1, background: '#f9fafb', borderRadius: 10, padding: '14px', textAlign: 'center' }}>
                        <div style={{ fontSize: 22, fontWeight: 800, color: '#059669' }}>
                            {((card?.currentPoints ?? 0) * redeemRatio).toFixed(2)} dhs
                        </div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Valeur totale</div>
                    </div>
                </div>

                {/* Points input */}
                <div>
                    <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
                        Points à utiliser
                    </label>
                    <input
                        type="number"
                        value={points}
                        min={1}
                        max={card?.currentPoints ?? 0}
                        onChange={e => setPoints(e.target.value)}
                        placeholder={`Max: ${card?.currentPoints ?? 0} pts`}
                        style={{
                            width: '100%',
                            padding: '10px 14px',
                            border: `1px solid ${pointsNum > (card?.currentPoints ?? 0) ? '#dc2626' : '#e5e7eb'}`,
                            borderRadius: 8,
                            fontSize: 14,
                            outline: 'none',
                            boxSizing: 'border-box',
                        }}
                    />
                    {pointsNum > (card?.currentPoints ?? 0) && (
                        <p style={{ fontSize: 12, color: '#dc2626', margin: '4px 0 0' }}>
                            Solde insuffisant
                        </p>
                    )}
                </div>

                {/* Discount preview */}
                {pointsNum > 0 && (
                    <div style={{
                        background: '#eff6ff',
                        border: '1px solid #bfdbfe',
                        borderRadius: 10,
                        padding: '14px 16px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}>
                        <div>
                            <div style={{ fontSize: 12, color: '#3b82f6', fontWeight: 600 }}>Réduction obtenue</div>
                            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                                {pointsNum} pts × {redeemRatio.toFixed(2)} dhs/pt
                            </div>
                        </div>
                        <div style={{ fontSize: 22, fontWeight: 800, color: '#1d4ed8' }}>
                            {discount} dhs
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
}
